import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Box, DialogContent, styled } from '@mui/material';
import { Alert } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { PartnerReInviteSchema } from 'FeatureModules/Partner/schemas/partners.schema';
import { DialogDefault, FieldWrapper, ThemeButton } from 'DesignSystem';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { REINVITE_PARTNER } from 'graphql/secure/mutations/partner.mutations';
import { GET_INVITED_PARTNER_USER } from 'graphql/secure/queries/companyUser.queries';
import React, { FC, useState } from 'react';
import { ICompanyUser } from 'FeatureModules/Auth/types/companyUser.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import ErrorState from 'Components/ErrorState';
import Icon from 'DesignSystem/Primitives/Icon/Icon';

const ContentWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

interface IFormInputs {
  email: string;
}

export interface IPartnerResendInviteDialogProps extends IDefaultDialogProps {
  companyId: string;
  resend?: boolean;
}

const PartnerResendInviteDialog: FC<IPartnerResendInviteDialogProps> = ({
  companyId,
  onClose,
  open = true,
}) => {
  const [error, setError] = useState<string>();
  const { setSuccessMessage } = useMessages();
  const { logEvent } = useLogEvent();
  const { data, loading } = useQuery<{ invitedPartnerCompanyUser: ICompanyUser }>(
    GET_INVITED_PARTNER_USER,
    {
      variables: { id: companyId },
    }
  );

  const [reInvitePartner] = useMutation<
    { invitePartner: { email: string; companyId: string } },
    { email: string; companyId: string }
  >(REINVITE_PARTNER, {
    onError: (error: ApolloError) => {
      if (error.message === 'USER_NOT_FOUND') {
        setError('The email address entered does not match any partner');
      } else {
        setError('There was an error re-inviting your partner');
      }
    },
    onCompleted: () => {
      setSuccessMessage('The partner was successfully re-invited to the platform.');
      logEvent('PARTNER_REINVITED');
      onClose?.();
    },
  });

  const user = data?.invitedPartnerCompanyUser;

  if (!data && loading) return <Loader />;
  if (!user) return <ErrorState action={undefined} />;

  const handleOnSubmit = async (values: IFormInputs) => {
    if (values.email) {
      await reInvitePartner({
        variables: {
          email: values.email,
          companyId,
        },
      });
    }
  };

  return (
    <DialogDefault
      title="Re-invite partner"
      onClose={onClose}
      open={open}
      fullWidth
      maxWidth="xs"
      background="#fff"
      preventCloseOnBackground
      data-testid="re-invite-partner-dialog"
    >
      <DialogContent>
        <ContentWrapper>
          <Formik
            initialValues={{ email: user.email }}
            validationSchema={PartnerReInviteSchema}
            onSubmit={handleOnSubmit}
          >
            {({ handleSubmit, isSubmitting }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {error && (
                    <Box mb={3}>
                      <Alert severity="error">{error}</Alert>
                    </Box>
                  )}
                  <Box mt={1} mb={4}>
                    <FieldWrapper label="Partner Email">
                      <Field
                        disabled
                        component={TextField}
                        fullWidth
                        name="email"
                        variant="outlined"
                      />
                    </FieldWrapper>
                  </Box>
                  <Box display="flex">
                    <Box mr={2}>
                      <ThemeButton color="BLUE_ICE" fullWidth size="large" onClick={onClose}>
                        Cancel
                      </ThemeButton>
                    </Box>
                    <ThemeButton
                      color="PRIMARY"
                      fullWidth
                      loading={isSubmitting}
                      startIcon={<Icon name="send" color="white" />}
                      size="large"
                      type="submit"
                      data-testid="re-send-invite-submit"
                    >
                      Re-send Invite
                    </ThemeButton>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </ContentWrapper>
      </DialogContent>
    </DialogDefault>
  );
};

export default PartnerResendInviteDialog;
